// Share-story composer + moments arbiter (web).
//
// LOCKSTEP: mirrors ios ShareStoryComposer (Models/ShareStory.swift) and
// Services/MomentsArbiter.swift. Change one → change both.
// Spec: docs/share-artifact-proposal.md (v4) + docs/hot-moments-proposal.md §2.

import { rackFill, rackEmoji } from './rack.js';
import { dayOfYearET } from './surpriseReveal.js';

// G2 Nightly Receipt text — the POSITIONAL ledger (🟡 per correct, ⚪ per
// miss, in reveal order) as a GRID in rows of six, under a dated header,
// over a score in words and the dare.
//
// LOCKSTEP with iOS ShareStoryComposer.nightlyReceiptText. This twin had
// DRIFTED: the iOS side took the grid + streak on 2026-08-04 (GRID-1) and
// this file kept the old one-line "Broke the scale" copy, so the two
// surfaces sent different receipts for eleven days. Re-synced 2026-08-05
// with Tom's header/score/closing pass. Change one → change both.
export function nightlyReceiptText(ledger, streak = 0, playDay = '') {
  const correct = ledger.filter(Boolean).length;
  const rows = [];
  for (let i = 0; i < ledger.length; i += 6) {
    rows.push(ledger.slice(i, i + 6).map((h) => (h ? '🟡' : '⚪')).join(''));
  }
  // Streak rides only at 2+ — a one-day streak is not a brag.
  const score = streak >= 2
    // "9/14 right" (Tom 2026-08-05, second pass): the words read long above
    // an already-compact grid, and the notation is legible HERE because the
    // grid directly above just showed what's being counted.
    ? `${correct}/${ledger.length} right · ${streak}-day streak`
    : `${correct}/${ledger.length} right`;
  const label = receiptDateLabel(playDay);
  const header = label ? `Predict Six — ${label}` : 'Predict Six';
  // "The news you play" replaced the category list (Tom 2026-08-12) — the
  // positioning line beats an inventory. Lockstep twin: iOS ShareStory.swift.
  return [
    header,
    ...rows,
    score,
    'The news you play',
    'Think you can do this?',
  ].join('\n');
}

// "2026-08-04" → "Aug 4"; '' on any shape surprise so the header degrades to
// a bare "Predict Six" rather than a dangling dash. Mirrors iOS
// ShareStoryComposer.receiptDateLabel.
function receiptDateLabel(playDay) {
  const parts = String(playDay).split('-');
  if (parts.length !== 3) return '';
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  if (!(m >= 1 && m <= 12) || !(d >= 1 && d <= 31)) return '';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[m - 1]} ${d}`;
}
import { toETDateString, playDayString } from './etDate.js';
import { logWebEvent } from '../firebase.js';

// ── Composer ──────────────────────────────────────────────────────────

export function storyText(story, streak = 0) {
  const flair = streak >= 3 ? ` · 🔥 ${streak}-day streak` : '';
  switch (story.kind) {
    case 'receipt_perfect':
      return `🟡🟡🟡🟡🟡🟡 👑 Ran the table — went ${story.correct}-for-${story.correct} tonight on Predict Six.${flair}`;
    case 'receipt_upset':
      return `Called it ✅ ${story.questionText} — ${story.crowdAgainstPercent}% of players said no way.${flair}`;
    case 'receipt_night': {
      const rack = rackEmoji(rackFill(story.correct, story.resolved));
      return `Went ${story.correct}-for-${story.resolved} tonight on Predict Six ${rack}${flair}\nThink you can beat me? Today's questions are live.`;
    }
    case 'sweat': {
      const rack = rackEmoji(rackFill(story.weekCorrect, story.weekResolved));
      let line = `${story.weekCorrect}-for-${story.weekResolved} this week on Predict Six ${rack} — ${story.openCalls} calls still live.${flair}`;
      if (story.legText) line += `\nNext leg: ${story.legText}`;
      return line;
    }
    case 'rivalry_result':
      // UX flywheel C2 — beating a named rival on a reveal night. No 8-ball
      // emoji, nothing like "took the night off". "today's results" is accurate
      // (fires on reveal nights). LOCKSTEP with iOS ShareStoryComposer.
      return `🏆 Beat ${story.opponentName} ${story.myCorrect}–${story.theirCorrect} in today's Predict Six results.\nThink you can take me? Today's six are live.`;
    default:
      return '';
  }
}

export function storyUrl(story, uid) {
  let url = 'https://predictsixapp.com';
  if (story.anchorQuestionId) {
    url += `/q/${story.anchorQuestionId}`;
    if (uid) url += `?ref=${uid}`;
  }
  return url;
}

/// The Take (play-day share): stance revealed — the stance IS the
/// provocation (Epic 1 decision: always reveal). The question text ships
/// in this body, so the /q OG image no longer overlays it (2026-07-02).
/// The 🔒×6 row (Tom 2026-07-04) is the pre-reveal teaser — six locks =
/// the sharer's six picks committed (this share is only reachable
/// post-submission, so it's always truthful).
///
/// "Play with me." (Tom 2026-07-05): the tail is a warm, direct invite —
/// NOT a wager announcement. It used to read "Your turn. 6 coins says I'm
/// right." (Tom 2026-07-04), but every today-view share now closes with the
/// friendlier line. The 6-coin challenge MECHANIC is unchanged and still
/// fires SILENTLY: when the tapper joins via the link's ?h2h= invite and
/// lands on the OTHER side of this question, the server auto-creates a
/// pending 6-coin challenge (attemptH2HInviteChallenge, source
/// "h2h_invite"). We simply stopped putting the wager in the copy. Callers
/// must ONLY use this text when the link carries ?h2h= (Play.jsx falls back
/// to plain text when the invite mint fails). LOCKSTEP with iOS
/// ShareStoryComposer.takeText.
export function takeText(questionText, answeredYes) {
  // `answeredYes` retained for call-site compatibility; intentionally unused
  // now that the stance is concealed (Tom 2026-07-06) — the answer-first invite
  // accept's post-commit reveal is the payoff, so the message must not spoil it.
  void answeredYes;
  // Just the locks, EVERY day (Tom 2026-07-29; Sunday folded in 2026-08-02
  // when the Sunday Six retired) — the question lives ON the /q card
  // (category color + ghost glyph + centered scrim), so the body doesn't
  // repeat it. The old Sunday branch existed because Sunday's card was
  // satirical art with no question text; that card is gone.
  // LOCKSTEP with iOS ShareStoryComposer.takeText.
  void questionText;
  return 'My six are locked in today 🔒🔒🔒🔒🔒🔒';
}

/// VP-2 (Tom 2026-07-16) — the text an approved PREDICTOR's question share
/// carries. Sibling of `takeText` above, under the same rule: it does not
/// spoil the pick.
///
/// Why the pick stays hidden, in the order that matters:
///   • CLICKS. "@tom says YES" hands the reader the payload in-feed — there's
///     no reason left to tap. "I've locked my call, what's yours?" can only be
///     resolved by tapping. For a tool whose whole job is turning impressions
///     into taps, the curiosity gap IS the product.
///   • THE LEAGUE. Revealing first makes a newcomer's first act copying
///     someone, and their first comparison a match by construction — the one
///     number a League exists to produce.
///
/// Differs from `takeText` ONLY in the tail: a peer share closes "Play with
/// me." (one friend, one H2H); a Predictor is broadcasting, and the invitation
/// is to make your own calls and measure them against theirs — which is what a
/// League is. LOCKSTEP with iOS ShareStoryComposer.predictorTeaseText.
export function predictorTeaseText(questionText) {
  return `I've made my call: ${questionText}\n🔒🔒🔒🔒🔒🔒 My six predictions for today are locked in. Make your calls and see if you can beat me.`;
}

/// THE REVEAL'S VERDICT — the one line that tells a player how to feel
/// about their night ("Ran the table." / "Holding your own."), and the line
/// the share button sits directly beneath on iOS.
///
/// LOCKSTEP with iOS RevealViewModel.performanceStatement: same five ratio
/// buckets, same pools in the same order, same ET day-of-year rotation
/// (a daily player must not read the identical line every night on the
/// app's highest-repetition surface). Below 50% it returns '' — the reveal
/// stays quiet rather than consoling, which is the reveal-surprise voice.
export function performanceStatement(correct, total) {
  if (!total || total < 1) return '';
  const ratio = correct / total;
  const pick = (pool) => pool[(dayOfYearET() - 1) % pool.length];
  if (correct === total) {
    // A full night (>= the rack's min sample) gets the brand idiom; any
    // other flawless run keeps the plain superlative, exactly as iOS splits
    // isPerfectNight from ratio >= 1.0.
    return total >= 1
      ? pick(['Ran the table.', 'Clean sweep.', 'Perfect read.', 'Untouchable.', "Didn't miss."])
      : pick(['Flawless.', 'Spotless.', 'Not a scratch.', 'Perfect card.']);
  }
  if (ratio >= 0.83) return pick(['Outstanding.', 'Razor sharp.', 'Dialed in.', 'Elite read.']);
  if (ratio >= 0.66) return pick(['Solid.', 'Strong night.', 'Well played.', 'Good reads.']);
  if (ratio >= 0.5) return pick(['Steady.', 'Holding your own.', 'On balance.', 'Even keel.']);
  return '';
}

/// The CHALLENGE ask, as two lines (Tom 2026-08-05):
///
///     Play me in Politics this week
///     Most correct calls wins!
///
/// Web's Play-a-Friend door had drifted onto the OLD one-line form ("Play me
/// at Politics this week — most correct wins."), embedded in a sentence that
/// then said "Play me" twice. Same door as the app's CHALLENGE A FRIEND, so it
/// gets the same words.
///
/// The exclamation is the one mark separating this from `calledItAsk`'s period
/// — Tom wrote each door's rule that way. Deliberate, not drift.
/// LOCKSTEP with iOS ShareStoryComposer.challengeAsk.
export function challengeAsk(category = null, withSix = false) {
  // UNIFORM, every door (Tom 2026-08-13, "YES" to matching the scoped
  // challenge): the arena still rides the LINK — the minted game and the
  // card name it — the text just stopped pre-explaining it. The params
  // stay for the call sites and are deliberately unread. LOCKSTEP with
  // iOS challengeAsk.
  return 'Play the news with me!';
}

/// The I CALLED IT ask (Tom 2026-08-12, second pass). "Think you can do
/// this?" replaced "Would you have called it?" — the brag's ask is a DARE,
/// and the softer question now belongs to the neutral shares, which keeps
/// the siblings distinct instead of identical. LOCKSTEP with iOS calledItAsk.
function calledItAsk() {
  return 'Think you can do this?\nToday\u2019s six questions are live.';
}

/// The I CALLED IT brag — six targets and a claim. The third symbol in the
/// family the receipt and the challenge already speak (🟡/⚪ graded,
/// 🔒 locked, 🎯 called it).
///
/// The question text lives on the CARD, not in this line: every caller pairs
/// it with a /s or /q link whose OG image renders the question and its
/// outcome better than a sentence can.
/// LOCKSTEP with iOS ShareStoryComposer.iCalledItText.
/// Line 1 is the whole story (Tom 2026-08-05, third pass): "66% said YES,
/// the answer was NO" — the crowd's call against the verdict, then a blank
/// line and the 🎯 claim. The 66% brag floor guarantees the two sides of
/// that comma always disagree. `crowdYesPercent` is the SIMPLE consensus
/// (the line prints the majority side's share); `answer` is the RESOLVED
/// outcome. LOCKSTEP with iOS ShareStoryComposer.iCalledItText.
export function iCalledItText(crowdYesPercent, answer) {
  const crowdSaidYes = crowdYesPercent >= 50;
  const pct = Math.round(crowdSaidYes ? crowdYesPercent : 100 - crowdYesPercent);
  return [
    `${pct}% of the crowd said ${crowdSaidYes ? 'YES' : 'NO'}`,
    `... but the answer was ${answer ? 'YES' : 'NO'}`,
    '',
    '🎯 I CALLED IT on Predict Six!',
    calledItAsk(),
  ].join('\n');
}

/// The neutral win — right, but in line with the crowd (Tom 2026-08-05).
/// Correct calls everyone else also made are still worth sharing; they're
/// just not "I CALLED IT", and dressing them that way devalues the times
/// the player actually beat the room.
/// LOCKSTEP with iOS ShareStoryComposer.rightWithCrowdText.
export function rightWithCrowdText() {
  return [
    '🔔 I got this one right.',
    'Would you have called it?',
    'Today\u2019s six questions are live.',
  ].join('\n');
}

/// Results-tab "SHARE QUESTION" text for a SETTLED question the sharer isn't
/// bragging about — a resolved miss, or a resolved question they didn't vote
/// on (Tom 2026-07-05). Parallels iCalledIt's shape but stays NEUTRAL about
/// the sharer's own result, so it's truthful either way. 🔔 = "here's how it
/// landed" (bell alert, no "verdict" framing — Tom 2026-07-07). LOCKSTEP with
/// iOS ShareStoryComposer.resolvedQuestionText.
export function resolvedQuestionText(questionText, correctAnswer) {
  // The question text is GONE (Tom 2026-08-12): the /q card already renders
  // it — repeating it said everything twice. `questionText` stays in the
  // signature (call sites) and is deliberately unread. LOCKSTEP with iOS.
  return `🔔 The answer was ${correctAnswer ? 'YES' : 'NO'}.\nWould you have called it?\nToday's six questions are live.`;
}

/// Results-tab "SHARE QUESTION" text for a still-OPEN day-after question (Tom
/// 2026-07-05) — its own emoji + prompt so it never reads like a settled
/// share. 🤔 = "still anyone's call." LOCKSTEP with iOS
/// ShareStoryComposer.openQuestionText.
export function openQuestionText(questionText) {
  // THREE LINES, question text dropped (Tom 2026-08-05) — the /q card it
  // ships with already renders the question.
  void questionText;
  return [
    "🤔 Still anyone's call.",
    "Today's six questions are live.",
    'Curious how your predictions will fare?',
  ].join('\n');
}

/// PRE-submit per-question invite (Tom 2026-07-05) — shared from the tappable
/// question image during the answer flow, before any answer is committed, so
/// it can't reveal a stance like takeText. Pure curiosity-and-challenge invite.
/// LOCKSTEP with iOS ShareStoryComposer.curiosityInviteText. (iOS wires this
/// today; kept here so the catalog stays in sync for a future web hook.)
export function curiosityInviteText(questionText) {
  return `👀 Check this out — ${questionText}\nPlay with me on Predict Six. It's free!`;
}

/// Share a story via navigator.share (clipboard fallback). Returns
/// { shared, usedClipboard }. Never throws.
export async function shareStoryLink(story, uid, streak = 0) {
  const text = storyText(story, streak);
  const url = storyUrl(story, uid);
  logWebEvent('web_story_shared', { subType: story.kind, questionId: story.anchorQuestionId || null });
  let shared = false;
  let usedClipboard = false;
  try {
    if (navigator.share) {
      await navigator.share({ title: 'Predict Six', text, url });
      shared = true;
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      shared = true;
      usedClipboard = true;
    }
  } catch {
    /* user cancelled the sheet, or unsupported → no share */
  }
  return { shared, usedClipboard };
}

// ── Moments arbiter (localStorage ledger) ─────────────────────────────
// Global cap: ONE growth prompt per ET day. Per-moment cooldowns (days),
// dismissal doubles the effective cooldown, 2 consecutive dismissals →
// 30 days of silence. Engagement resets the count.

const GLOBAL_KEY = 'ps_moments_last_day';
const SILENCE_DAYS = 30;

function shownKey(id) {
  return `ps_moment_${id}_shown`;
}
function dismissKey(id) {
  return `ps_moment_${id}_dismissals`;
}

function daysSince(ts) {
  if (!ts) return Infinity;
  return Math.max(0, (Date.now() - ts) / 86400000);
}

export function momentIdFor(story) {
  return `reveal_share_${story.kind}`;
}

export function momentCooldownDays(story) {
  return story.kind === 'receipt_perfect' ? 0 : 3;
}

export function canOfferMoment(momentId, cooldownDays) {
  try {
    if (localStorage.getItem(GLOBAL_KEY) === toETDateString()) return false;
    const dismissals = Number(localStorage.getItem(dismissKey(momentId)) || 0);
    const lastShown = Number(localStorage.getItem(shownKey(momentId)) || 0);
    if (dismissals >= 2 && daysSince(lastShown) < SILENCE_DAYS) return false;
    const effective = cooldownDays === 0 ? 0 : cooldownDays * 2 ** Math.min(dismissals, 2);
    if (effective > 0 && daysSince(lastShown) < effective) return false;
    return true;
  } catch {
    return false; // storage unavailable → never prompt (safe default)
  }
}

export function recordMomentShown(momentId) {
  try {
    localStorage.setItem(shownKey(momentId), String(Date.now()));
    localStorage.setItem(GLOBAL_KEY, toETDateString());
  } catch {
    /* non-fatal */
  }
  logWebEvent('moment_shown', { moment: momentId });
}

export function recordMomentEngaged(momentId) {
  try {
    localStorage.setItem(dismissKey(momentId), '0');
  } catch {
    /* non-fatal */
  }
  logWebEvent('moment_engaged', { moment: momentId });
}

export function recordMomentDismissed(momentId) {
  try {
    const n = Number(localStorage.getItem(dismissKey(momentId)) || 0) + 1;
    localStorage.setItem(dismissKey(momentId), String(n));
  } catch {
    /* non-fatal */
  }
  logWebEvent('moment_dismissed', { moment: momentId });
}
